/**
 * Block Service
 * Unified service for user blocking operations.
 * Handles blocking, unblocking, and querying block status.
 * Never throws exceptions - returns status codes instead.
 * 
 * IMPORTANT: All functions accept userId as parameter.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 * Use getCachedUserId() from AuthContext to get the user ID.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import { getCachedUserId } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type ChatParticipant = Database['public']['Tables']['chat_participants']['Row'];

export type BlockStatus = 'blocked' | 'not_blocked' | 'unknown';

export interface BlockCheckResult {
  status: BlockStatus;
  error?: string;
}

export interface BlockActionResult {
  success: boolean;
  status: BlockStatus;
  error?: string;
}

export interface BlockedUserInfo {
  id: string;
  blockedUserId: string;
  createdAt: string;
}

export interface ChatRoomWithBlockInfo {
  id: string;
  context_entity_id: string;
  context_entity_type: string;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  creator_user_id: string | null;
  kind: string | null;
  participants: ChatParticipant[];
  otherParticipant?: { userId: string };
  lastMessage: null;
  unreadCount: number;
}

/**
 * Check if target user has blocked the current user
 * Returns explicit status - NEVER throws
 * 
 * @param targetUserId - The user who may have blocked us
 * @param currentUserId - Optional: current user ID (uses cache if not provided)
 */
export async function isBlockedBy(targetUserId: string, currentUserId?: string): Promise<BlockCheckResult> {
  try {
    const userId = currentUserId ?? getCachedUserId();
    
    if (!userId) {
      logger.warn('Block check attempted without auth', {
        context: { feature: 'blocks', function_name: 'isBlockedBy' },
        error_code: 'block_check_no_auth',
        error_type: 'auth',
      });
      return { status: 'unknown', error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('user_blocks')
      .select('id')
      .eq('blocker_user_id', targetUserId)
      .eq('blocked_user_id', userId)
      .maybeSingle();

    if (error) {
      logger.warn('Block check query failed', {
        context: { feature: 'blocks', function_name: 'isBlockedBy' },
        error,
        error_code: 'block_check_query_failed',
        error_type: 'network',
      });
      return { status: 'unknown', error: error.message };
    }

    return { status: data ? 'blocked' : 'not_blocked' };
  } catch (e) {
    logger.warn('Block check unexpected error', {
      context: { feature: 'blocks', function_name: 'isBlockedBy' },
      error: e,
      error_code: 'block_check_unexpected',
      error_type: 'unexpected',
    });
    return { status: 'unknown', error: 'Unexpected error' };
  }
}

/**
 * Check if current user has blocked target user
 * Returns explicit status - NEVER throws
 * 
 * @param targetUserId - The user we may have blocked
 * @param currentUserId - Optional: current user ID (uses cache if not provided)
 */
export async function hasBlocked(targetUserId: string, currentUserId?: string): Promise<BlockCheckResult> {
  try {
    const userId = currentUserId ?? getCachedUserId();
    
    if (!userId) {
      return { status: 'unknown', error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('user_blocks')
      .select('id')
      .eq('blocker_user_id', userId)
      .eq('blocked_user_id', targetUserId)
      .maybeSingle();

    if (error) {
      logger.warn('Has blocked check failed', {
        context: { feature: 'blocks', function_name: 'hasBlocked' },
        error,
        error_code: 'has_blocked_query_failed',
        error_type: 'network',
      });
      return { status: 'unknown', error: error.message };
    }

    return { status: data ? 'blocked' : 'not_blocked' };
  } catch (e) {
    return { status: 'unknown', error: 'Unexpected error' };
  }
}

/**
 * Block a user - returns success/failure with status
 * 
 * @param targetUserId - The user to block
 * @param currentUserId - Optional: current user ID (uses cache if not provided)
 */
export async function blockUser(targetUserId: string, currentUserId?: string): Promise<BlockActionResult> {
  try {
    const userId = currentUserId ?? getCachedUserId();
    
    if (!userId) {
      return { success: false, status: 'unknown', error: 'User not authenticated' };
    }

    const { error } = await supabase
      .from('user_blocks')
      .insert({
        blocker_user_id: userId,
        blocked_user_id: targetUserId,
      });

    if (error) {
      // Check for duplicate
      if (error.code === '23505') {
        return { success: true, status: 'blocked' }; // Already blocked
      }
      
      logger.error('Block user failed', {
        context: { feature: 'blocks', function_name: 'blockUser' },
        error,
        error_code: 'block_user_failed',
        error_type: 'network',
      });
      return { success: false, status: 'unknown', error: error.message };
    }

    return { success: true, status: 'blocked' };
  } catch (e) {
    return { success: false, status: 'unknown', error: 'Unexpected error' };
  }
}

/**
 * Unblock a user - returns success/failure with status
 * 
 * @param targetUserId - The user to unblock
 * @param currentUserId - Optional: current user ID (uses cache if not provided)
 */
export async function unblockUser(targetUserId: string, currentUserId?: string): Promise<BlockActionResult> {
  try {
    const userId = currentUserId ?? getCachedUserId();
    
    if (!userId) {
      return { success: false, status: 'unknown', error: 'User not authenticated' };
    }

    const { error } = await supabase
      .from('user_blocks')
      .delete()
      .eq('blocker_user_id', userId)
      .eq('blocked_user_id', targetUserId);

    if (error) {
      logger.error('Unblock user failed', {
        context: { feature: 'blocks', function_name: 'unblockUser' },
        error,
        error_code: 'unblock_user_failed',
        error_type: 'network',
      });
      return { success: false, status: 'unknown', error: error.message };
    }

    return { success: true, status: 'not_blocked' };
  } catch (e) {
    return { success: false, status: 'unknown', error: 'Unexpected error' };
  }
}

/**
 * Get list of users I've blocked
 * 
 * @param currentUserId - Optional: current user ID (uses cache if not provided)
 */
export async function getBlockedUsers(currentUserId?: string): Promise<{ data: BlockedUserInfo[] | null; error: Error | null }> {
  try {
    const userId = currentUserId ?? getCachedUserId();
    
    if (!userId) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('user_blocks')
      .select('id, blocked_user_id, created_at')
      .eq('blocker_user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error fetching blocked users', {
        context: { feature: 'blocks', function_name: 'getBlockedUsers' },
        error,
        error_code: 'get_blocked_users_failed',
        error_type: 'network',
      });
      return { data: null, error };
    }

    return {
      data: (data || []).map(b => ({
        id: b.id,
        blockedUserId: b.blocked_user_id,
        createdAt: b.created_at,
      })),
      error: null,
    };
  } catch (e) {
    logger.error('Unexpected error fetching blocked users', {
      context: { feature: 'blocks', function_name: 'getBlockedUsers' },
      error: e,
      error_code: 'get_blocked_users_unexpected',
      error_type: 'unexpected',
    });
    return { data: null, error: e as Error };
  }
}

/**
 * Get blocked chats (chats where I've blocked the other user)
 * 
 * @param currentUserId - Optional: current user ID (uses cache if not provided)
 */
export async function getBlockedChats(currentUserId?: string): Promise<{ data: ChatRoomWithBlockInfo[] | null; error: Error | null }> {
  try {
    const userId = currentUserId ?? getCachedUserId();
    
    if (!userId) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Get list of users I've blocked
    const { data: blocks, error: blocksError } = await supabase
      .from('user_blocks')
      .select('blocked_user_id')
      .eq('blocker_user_id', userId);

    if (blocksError) {
      logger.error('Error fetching blocks for chats', {
        context: { feature: 'blocks', function_name: 'getBlockedChats' },
        error: blocksError,
        error_code: 'blocked_chats_blocks_failed',
        error_type: 'network',
      });
      return { data: null, error: blocksError };
    }
    
    if (!blocks || blocks.length === 0) {
      return { data: [], error: null };
    }

    const blockedUserIds = blocks.map(b => b.blocked_user_id);

    // Get chat rooms where I'm a participant
    const { data: participations } = await supabase
      .from('chat_participants')
      .select('chat_room_id')
      .eq('user_id', userId);

    if (!participations || participations.length === 0) {
      return { data: [], error: null };
    }

    const roomIds = participations.map(p => p.chat_room_id);

    // Get rooms with participants
    const { data: rooms, error: roomsError } = await supabase
      .from('chat_rooms')
      .select(`
        *,
        chat_participants (
          id,
          user_id,
          role,
          last_read_at,
          created_at,
          chat_room_id,
          role_codifier_key,
          deleted_at
        )
      `)
      .in('id', roomIds)
      .is('deleted_at', null);

    if (roomsError) {
      logger.error('Error fetching rooms for blocked chats', {
        context: { feature: 'blocks', function_name: 'getBlockedChats' },
        error: roomsError,
        error_code: 'blocked_chats_rooms_failed',
        error_type: 'network',
      });
      return { data: null, error: roomsError };
    }

    // Filter to rooms where the other participant is blocked
    const blockedRooms = (rooms || []).filter(room => {
      const otherParticipant = room.chat_participants?.find(
        (p: ChatParticipant) => p.user_id !== userId
      );
      return otherParticipant && blockedUserIds.includes(otherParticipant.user_id);
    });

    const roomsWithDetails: ChatRoomWithBlockInfo[] = blockedRooms.map(room => {
      const otherParticipant = room.chat_participants?.find(
        (p: ChatParticipant) => p.user_id !== userId
      );

      return {
        id: room.id,
        context_entity_id: room.context_entity_id,
        context_entity_type: room.context_entity_type,
        last_message_at: room.last_message_at,
        created_at: room.created_at,
        updated_at: room.updated_at,
        deleted_at: room.deleted_at,
        creator_user_id: room.creator_user_id,
        kind: room.kind,
        participants: room.chat_participants || [],
        lastMessage: null,
        unreadCount: 0,
        otherParticipant: otherParticipant ? {
          userId: otherParticipant.user_id,
        } : undefined,
      };
    });

    return { data: roomsWithDetails, error: null };
  } catch (e) {
    logger.error('Error fetching blocked chats', {
      context: { feature: 'blocks', function_name: 'getBlockedChats' },
      error: e,
      error_code: 'blocked_chats_fetch_failed',
      error_type: 'unexpected',
    });
    return { data: null, error: e as Error };
  }
}

export const blockService = {
  isBlockedBy,
  hasBlocked,
  blockUser,
  unblockUser,
  getBlockedUsers,
  getBlockedChats,
};
